import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../redux/store';
import {
    uploadCensusFile,
    fetchCensusFiles,
    fetchCensusStatistics,
    downloadCensusFile,
    deleteCensusFile,
    clearError
} from '../redux/slices/censusSlice';
import { tokenManager } from '../utils/tokenManager';
import styles from './CensusDataUploader.module.css';
import ReturnToDashboard from './ReturnToDashboard';
import { FaDownload, FaUpload, FaChartBar, FaFileAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartData,
    ChartOptions
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const CensusDataUploader: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        files,
        statistics,
        loading,
        uploading,
        downloading,
        error,
        uploadProgress
    } = useSelector((state: RootState) => state.census);

    // Form state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [employerName, setEmployerName] = useState('');
    const [planCode, setPlanCode] = useState('');
    const [planName, setPlanName] = useState('');
    const [planOwnerCarrier, setPlanOwnerCarrier] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [uploadWarnings, setUploadWarnings] = useState<string[]>([]);

    // Filters state
    const [filters, setFilters] = useState({
        employer_name: '',
        plan_code: '',
        status: ''
    });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const filesPerPage = 10;
    
    // Modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmationData, setConfirmationData] = useState<{
        message: string;
        onConfirm: () => void;
        onCancel: () => void;
    } | null>(null);
    
    // Error popup state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        // Load initial data
        dispatch(fetchCensusFiles({}));
        dispatch(fetchCensusStatistics());
    }, [dispatch]);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            
            // Validate file type for drag & drop too
            if (!validateFileType(file)) {
                showError('❌ Invalid file type! Please drop only XLSX, XLS, or CSV files.');
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const validateFileType = (file: File): boolean => {
        const allowedTypes = ['.xlsx', '.xls', '.csv'];
        const fileName = file.name.toLowerCase();
        return allowedTypes.some(type => fileName.endsWith(type));
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            
            // Validate file type
            if (!validateFileType(file)) {
                showError('❌ Invalid file type! Please select only XLSX, XLS, or CSV files.');
                e.target.value = ''; // Clear the input
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const handleUpload = async (initialForceUpload = false) => {
        let forceUpload = initialForceUpload;
        if (!selectedFile || !employerName || !planCode || !planName || !planOwnerCarrier) {
            showError('Please fill in all required fields and select a file');
            return;
        }

        setUploadWarnings([]);
        dispatch(clearError());

        // First, validate the file if not forcing upload
        if (!forceUpload) {
            try {
                const token = await tokenManager.getToken();
                const authType = tokenManager.getAuthType();
                
                const formData = new FormData();
                formData.append('file', selectedFile);
                formData.append('employer_name', employerName);
                formData.append('plan_code', planCode);
                if (planName) formData.append('plan_name', planName);
                if (planOwnerCarrier) formData.append('plan_owner_carrier', planOwnerCarrier);

                const validateResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/dev/api/v1'}/census/validate`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-Auth-Type': authType || 'aws'
                    },
                    body: formData
                });

                const validationResult = await validateResponse.json();
                console.log('Validation result:', validationResult); // Debug log
                
                if (validationResult.warnings && validationResult.warnings.length > 0) {
                    const hasActionRequired = validationResult.warnings.some((w: any) => w.action_required);
                    console.log('Has action required:', hasActionRequired); // Debug log
                    
                    if (hasActionRequired) {
                        const warningMessage = validationResult.warnings
                            .filter((w: any) => w.action_required)
                            .map((w: any) => w.message || w)
                            .join('\n\n');
                        
                        const userConfirmed = await showConfirmation(
                            `⚠️ Warning:\n\n${warningMessage}\n\nDo you want to continue with the upload?`
                        );
                        
                        if (!userConfirmed) {
                            return; // User cancelled
                        }
                        
                        // Set force upload to true after confirmation
                        console.log('User confirmed, setting force_upload=true'); // Debug log
                        forceUpload = true;
                    }
                }
            } catch (error) {
                console.error('Validation failed:', error);
                // Continue with upload even if validation fails
            }
        }

        try {
            console.log('Final upload call with force_upload:', forceUpload); // Debug log
            const result = await dispatch(uploadCensusFile({
                file: selectedFile,
                employer_name: employerName,
                plan_code: planCode,
                plan_name: planName,
                plan_owner_carrier: planOwnerCarrier,
                uploaded_by: 'BMP User',
                force_upload: forceUpload
            }) as any);

            if (result.payload?.warnings) {
                setUploadWarnings(result.payload.warnings);
            }

            if (result.payload?.success) {
                // Reset form
                setSelectedFile(null);
                setEmployerName('');
                setPlanCode('');
                setPlanName('');
                setPlanOwnerCarrier('');
                
                // Refresh data
                dispatch(fetchCensusFiles({}));
                dispatch(fetchCensusStatistics());
            }
        } catch (error) {
            console.error('Upload failed:', error);
        }
    };

    const handleDownload = (fileId: number) => {
        dispatch(downloadCensusFile(fileId));
    };

    const handleDelete = async (fileId: number) => {
        if (window.confirm('Are you sure you want to delete this file?')) {
            await dispatch(deleteCensusFile(fileId));
            dispatch(fetchCensusFiles({}));
            dispatch(fetchCensusStatistics());
        }
    };

    const applyFilters = () => {
        dispatch(fetchCensusFiles(filters));
    };

    const clearFilters = () => {
        setFilters({ employer_name: '', plan_code: '', status: '' });
        dispatch(fetchCensusFiles({}));
    };

    const handleDownloadMetadata = async () => {
        if (!startDate || !endDate) {
            showError('Please select both start and end dates');
            return;
        }

        try {
            const token = await tokenManager.getToken();
            const authType = tokenManager.getAuthType();
            
            const queryParams = new URLSearchParams({
                start_date: startDate,
                end_date: endDate
            });

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/dev/api/v1'}/census/metadata/download?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Auth-Type': authType || 'aws'
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `census-metadata-${startDate}-to-${endDate}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                throw new Error('Failed to download metadata');
            }
        } catch (error) {
            console.error('Download failed:', error);
            showError('Failed to download metadata. Please try again.');
        }
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
    };

    // Custom confirmation modal
    const showConfirmation = (message: string): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmationData({
                message,
                onConfirm: () => {
                    setShowConfirmModal(false);
                    setConfirmationData(null);
                    resolve(true);
                },
                onCancel: () => {
                    setShowConfirmModal(false);
                    setConfirmationData(null);
                    resolve(false);
                }
            });
            setShowConfirmModal(true);
        });
    };

    const showError = (message: string) => {
        setErrorMessage(message);
        setShowErrorModal(true);
    };

    const hideError = () => {
        setShowErrorModal(false);
        setErrorMessage('');
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'uploaded': return '#2196F3';
            case 'processed': return '#4CAF50';
            case 'failed': return '#F44336';
            default: return '#757575';
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'uploaded':
                return { backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' };
            case 'processed':
                return { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' };
            case 'failed':
                return { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' };
            default:
                return { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' };
        }
    };

    // Pagination calculations
    const getPaginatedFiles = () => {
        const startIndex = (currentPage - 1) * filesPerPage;
        const endIndex = startIndex + filesPerPage;
        return files.slice(startIndex, endIndex);
    };

    const totalPages = Math.ceil(files.length / filesPerPage);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.pageHeader}>
                <ReturnToDashboard />
            </div>
            <div className={styles.header}>
                <h1>Census Data Uploader</h1>
            </div>

            {/* Upload Section */}
            <div className={styles.uploadSection}>
                <h2><FaUpload className={styles.sectionIcon} /> Upload Census File</h2>
                
                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label>Employer Name *</label>
                        <input
                            type="text"
                            value={employerName}
                            onChange={(e) => setEmployerName(e.target.value)}
                            placeholder="Enter employer name"
                            required
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Coupon Code *</label>
                        <input
                            type="text"
                            value={planCode}
                            onChange={(e) => setPlanCode(e.target.value)}
                            placeholder="Enter coupon code"
                            required
                        />
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label>Plan Name *</label>
                        <input
                            type="text"
                            value={planName}
                            onChange={(e) => setPlanName(e.target.value)}
                            placeholder="e.g. AirMedCare Network Monthly Membership"
                            required
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Plan Owner/Carrier *</label>
                        <input
                            type="text"
                            value={planOwnerCarrier}
                            onChange={(e) => setPlanOwnerCarrier(e.target.value)}
                            placeholder="e.g. AirMedCare"
                            required
                        />
                    </div>
                </div>

                <div 
                    className={`${styles.dropZone} ${dragActive ? styles.dragActive : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        id="fileInput"
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls,.csv"
                        className={styles.fileInput}
                    />
                    <label htmlFor="fileInput" className={styles.fileInputLabel}>
                        {selectedFile ? (
                            <div className={styles.selectedFile}>
                                <span className={styles.fileName}>{selectedFile.name}</span>
                                <span className={styles.fileSize}>({formatFileSize(selectedFile.size)})</span>
                            </div>
                        ) : (
                            <div className={styles.dropMessage}>
                                <div className={styles.uploadIcon}>📁</div>
                                <p>Drag and drop your census file here or click to browse</p>
                                <p className={styles.fileTypes}>Supported formats: .xlsx, .xls, .csv (Max 100MB)</p>
                            </div>
                        )}
                    </label>
                </div>


                {uploading && (
                    <div className={styles.uploadProgress}>
                        <div className={styles.progressBar}>
                            <div 
                                className={styles.progressFill} 
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                        <p>Uploading... {uploadProgress}%</p>
                    </div>
                )}

                <button 
                    onClick={() => handleUpload()}
                    disabled={!selectedFile || !employerName || !planCode || !planName || !planOwnerCarrier || uploading}
                    className={styles.uploadButton}
                >
                    {uploading ? 'Uploading...' : 'Upload File'}
                </button>

                {error && (
                    <div className={styles.error}>
                        <p>❌ {error}</p>
                    </div>
                )}
            </div>

            {/* Download Files Metadata Section */}
            <div className={styles.downloadSection}>
                <h2><FaDownload className={styles.sectionIcon} /> Download Files Metadata</h2>
                <p>Select a date range to download metadata for all census files processed within that period. The Excel file will contain file details, upload dates, and processing status.</p>
                
                <div className={styles.dateRangeForm}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Start Date *</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={styles.input}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>End Date *</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={styles.input}
                                required
                            />
                        </div>
                    </div>
                    
                    <div className={styles.downloadActions}>
                        <button 
                            className={styles.downloadButton}
                            onClick={handleDownloadMetadata}
                            disabled={!startDate || !endDate || downloading}
                        >
                            {downloading ? '📥 Downloading...' : '📥 Download Metadata'}
                        </button>
                        <button 
                            className={styles.clearButton}
                            onClick={handleClearDates}
                        >
                            Clear Dates
                        </button>
                    </div>
                    
                    <div className={styles.downloadInfo}>
                        <p>ℹ️ <strong>Download Information</strong></p>
                        <p>Select a date range to download metadata for all census files processed within that period. The Excel file will contain file details, upload dates, and processing status.</p>
                    </div>
                </div>
            </div>

            {/* Statistics Section */}
            {statistics && (
                <div className={styles.statisticsSection}>
                    <h2><FaChartBar className={styles.sectionIcon} /> Upload Statistics</h2>
                    
                    {/* Stats Cards */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon}>
                                <FaFileAlt />
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statNumber}>{statistics.total_files}</span>
                                <span className={styles.statLabel}>Total Files</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#3b82f6' }}>
                                <FaUpload />
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statNumber}>{statistics.uploaded}</span>
                                <span className={styles.statLabel}>Uploaded</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#10b981' }}>
                                <FaCheckCircle />
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statNumber}>{statistics.processed}</span>
                                <span className={styles.statLabel}>Processed</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#ef4444' }}>
                                <FaExclamationTriangle />
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statNumber}>{statistics.failed}</span>
                                <span className={styles.statLabel}>Failed</span>
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statIcon} style={{ color: '#8b5cf6' }}>
                                <FaFileAlt />
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statNumber}>{statistics.total_size_mb} MB</span>
                                <span className={styles.statLabel}>Total Size</span>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className={styles.chartsContainer}>
                        <div className={styles.chartWrapper}>
                            <h3>File Status Distribution</h3>
                            <div className={styles.chartInner}>
                                <Doughnut 
                                    data={{
                                        labels: ['Uploaded', 'Processed', 'Failed'],
                                        datasets: [{
                                            data: [
                                                statistics.uploaded,
                                                statistics.processed,
                                                statistics.failed
                                            ],
                                            backgroundColor: [
                                                '#3b82f6',
                                                '#10b981',
                                                '#ef4444'
                                            ],
                                            borderColor: '#ffffff',
                                            borderWidth: 2
                                        }]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        cutout: '65%',
                                        plugins: {
                                            legend: {
                                                position: 'bottom',
                                                labels: {
                                                    padding: 20,
                                                    usePointStyle: true
                                                }
                                            },
                                            tooltip: {
                                                backgroundColor: '#1f2937',
                                                titleColor: '#ffffff',
                                                bodyColor: '#ffffff',
                                                borderColor: '#374151',
                                                borderWidth: 1,
                                                callbacks: {
                                                    label: function(context: any) {
                                                        const label = context.label || '';
                                                        const value = context.raw || 0;
                                                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                                                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                                                        return `${label}: ${value} (${percentage}%)`;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        
                        <div className={styles.chartWrapper}>
                            <h3>Processing Overview</h3>
                            <div className={styles.chartInner}>
                                <Bar 
                                    data={{
                                        labels: ['Files'],
                                        datasets: [
                                            {
                                                label: 'Processed',
                                                data: [statistics.processed],
                                                backgroundColor: '#10b981',
                                                borderColor: '#059669',
                                                borderWidth: 1
                                            },
                                            {
                                                label: 'Failed',
                                                data: [statistics.failed],
                                                backgroundColor: '#ef4444',
                                                borderColor: '#dc2626',
                                                borderWidth: 1
                                            },
                                            {
                                                label: 'Pending',
                                                data: [statistics.uploaded],
                                                backgroundColor: '#3b82f6',
                                                borderColor: '#2563eb',
                                                borderWidth: 1
                                            }
                                        ]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: {
                                                    stepSize: 1
                                                }
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                position: 'bottom',
                                                labels: {
                                                    padding: 20,
                                                    usePointStyle: true
                                                }
                                            },
                                            tooltip: {
                                                backgroundColor: '#1f2937',
                                                titleColor: '#ffffff',
                                                bodyColor: '#ffffff',
                                                borderColor: '#374151',
                                                borderWidth: 1
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Files Section */}
            <div className={styles.filesSection}>
                <div className={styles.sectionHeader}>
                    <h2><FaFileAlt className={styles.sectionIcon} /> Uploaded Files</h2>
                    <p className={styles.filesNote}>Showing latest 50 files with pagination (10 per page)</p>
                    
                    {/* Filters */}
                    <div className={styles.filters}>
                        <input
                            type="text"
                            placeholder="Filter by employer"
                            value={filters.employer_name}
                            onChange={(e) => setFilters({...filters, employer_name: e.target.value})}
                        />
                        <input
                            type="text"
                            placeholder="Filter by coupon code"
                            value={filters.plan_code}
                            onChange={(e) => setFilters({...filters, plan_code: e.target.value})}
                        />
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({...filters, status: e.target.value})}
                        >
                            <option value="">All Statuses</option>
                            <option value="uploaded">Uploaded</option>
                            <option value="processed">Processed</option>
                            <option value="failed">Failed</option>
                        </select>
                        <button onClick={applyFilters} className={styles.filterButton}>Apply</button>
                        <button onClick={clearFilters} className={styles.clearButton}>Clear</button>
                    </div>
                </div>

                {loading ? (
                    <div className={styles.loading}>Loading files...</div>
                ) : (
                    <>
                        <div className={styles.filesTable}>
                            <div className={styles.tableHeader}>
                                <span>File Name</span>
                                <span>Employer</span>
                                <span>Coupon Code</span>
                                <span>Size</span>
                                <span>Status</span>
                                <span>Upload Date</span>
                            </div>
                            
                            {getPaginatedFiles().map((file) => (
                                <div key={file.id} className={styles.tableRow}>
                                    <span className={styles.fileName} title={file.original_filename}>
                                        {file.original_filename}
                                    </span>
                                    <span className={styles.employer} title={file.employer_name}>
                                        {file.employer_name}
                                    </span>
                                    <span className={styles.planCode} title={file.plan_code}>
                                        {file.plan_code}
                                    </span>
                                    <span className={styles.fileSize}>
                                        {formatFileSize(file.file_size_bytes || file.file_size || 0)}
                                    </span>
                                    <span 
                                        className={styles.status}
                                        style={getStatusStyle(file.processing_status)}
                                    >
                                        {file.processing_status.toUpperCase()}
                                    </span>
                                    <span className={styles.uploadDate}>
                                        {formatDate(file.upload_date)}
                                    </span>
                                </div>
                            ))}
                            
                            {files.length === 0 && (
                                <div className={styles.noFiles}>
                                    No files found. Upload your first census file above.
                                </div>
                            )}
                        </div>
                        
                        {/* Pagination */}
                        {files.length > filesPerPage && (
                            <div className={styles.pagination}>
                                <button 
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={styles.paginationButton}
                                >
                                    ← Previous
                                </button>
                                
                                <div className={styles.pageNumbers}>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className={`${styles.pageNumber} ${currentPage === page ? styles.active : ''}`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>
                                
                                <button 
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={styles.paginationButton}
                                >
                                    Next →
                                </button>
                                
                                <span className={styles.pageInfo}>
                                    Page {currentPage} of {totalPages} • Showing {getPaginatedFiles().length} of {files.length} files
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>


            {/* Custom Confirmation Modal */}
            {showConfirmModal && confirmationData && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>⚠️ Confirmation Required</h3>
                        </div>
                        <div className={styles.modalBody}>
                            <p>{confirmationData.message}</p>
                        </div>
                        <div className={styles.modalActions}>
                            <button 
                                className={styles.cancelButton}
                                onClick={confirmationData.onCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className={styles.confirmButton}
                                onClick={confirmationData.onConfirm}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Error Modal */}
            {showErrorModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>❌ Error</h3>
                        </div>
                        <div className={styles.modalBody}>
                            <p>{errorMessage}</p>
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                className={styles.confirmButton}
                                onClick={hideError}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CensusDataUploader;
